import DatafieldSection from "../../../components/datafield-section";
import {
  RetailODDetails,
  RetailOdLedgerInfoDto,
  SubLoanLedgerInfo,
  TermLoanDetails,
  TermLoanLedgerInfoDto
} from "../../../dto";
import { FieldLabel } from "../../../models";
import { refactorLoanData } from "../../loan-details/helpers";
import { ExtendedLoanType } from "../types";

interface CurrentLoanAccountStatusProps {
  loanDetails?: TermLoanDetails | RetailODDetails | null;
  termLoanLedgerInfo?: TermLoanLedgerInfoDto | null;
  retailOdLedgerInfo?: RetailOdLedgerInfoDto | null;
  subLoanLedgerInfo?: SubLoanLedgerInfo | null;
  isDrawdownLoan?: boolean;
  unpaidRepaymentCommitmentAmount?: number | null;
}

const LOAN_ACCOUNT_STATUS_LEFT_LABEL_TO_LOAN_TYPE_MAP: Record<
  ExtendedLoanType,
  FieldLabel[]
> = {
  TERM_LOAN: [
    {
      label: "Principal Outstanding",
      key: "totalPrincipalOutstanding",
      prefix: "Rs",
      formatMethod: (value: number) => value.toFixed(2),
    },
    {
      label: "Total Interest To Be Paid",
      key: "totalInterestOutstanding",
      prefix: "Rs",
      formatMethod: (value: number) => value.toFixed(2),
    },
  ],
  RETAIL_OD: [
    {
      label: "Principal Outstanding",
      key: "totalPrincipalOutstanding",
      prefix: "Rs",
      formatMethod: (value: number) => value.toFixed(2),
    },
    {
      label: "Total Interest To Be Paid",
      key: "totalInterestOutstanding",
      prefix: "Rs",
      formatMethod: (value: number) => value.toFixed(2),
    },
    {
      label: "Total Drawn Amount",
      key: "totalAmountWithdrawn",
      prefix: "Rs",
      formatMethod: (value: number) => value.toFixed(2),
    },
  ],
  RETAIL_OD_DRAWDOWN: [
    {
      label: "Principal Outstanding",
      key: "totalPrincipalOutstanding",
      prefix: "Rs",
      formatMethod: (value: number) => value.toFixed(2),
    },
    {
      label: "Total Interest To Be Paid",
      key: "totalInterestOutstanding",
      prefix: "Rs",
      formatMethod: (value: number) => value.toFixed(2),
    },
  ],
};

const LOAN_ACCOUNT_STATUS_UNPAID_COMMITMENT_AMOUNT_LABEL_TO_LOAN_TYPE_MAP: Partial<
  Record<ExtendedLoanType, FieldLabel[]>
> = {
  TERM_LOAN: [
    {
      label: "Unpaid Commitment Amount",
      key: "unpaidCommitmentAmount",
      prefix: "Rs",
      formatMethod: (value: number) => value.toFixed(2),
    },
  ],
  RETAIL_OD_DRAWDOWN: [
    {
      label: "Unpaid Commitment Amount",
      key: "unpaidCommitmentAmount",
      prefix: "Rs",
      formatMethod: (value: number) => value.toFixed(2),
    },
  ],
};

export default function CurrentLoanAccountStatus({
  loanDetails,
  termLoanLedgerInfo,
  retailOdLedgerInfo,
  subLoanLedgerInfo,
  isDrawdownLoan,
  unpaidRepaymentCommitmentAmount,
}: CurrentLoanAccountStatusProps) {
  if (!loanDetails && !subLoanLedgerInfo) return null;

  const loanType = isDrawdownLoan
    ? "RETAIL_OD_DRAWDOWN"
    : ((loanDetails?.loanType ?? "TERM_LOAN") as ExtendedLoanType);

  const data = isDrawdownLoan
    ? subLoanLedgerInfo
    : refactorLoanData(loanDetails, termLoanLedgerInfo, retailOdLedgerInfo);

  const leftLabels = LOAN_ACCOUNT_STATUS_LEFT_LABEL_TO_LOAN_TYPE_MAP[loanType];

  if (!leftLabels) return null;

  const unpaidCommitmentAmountLabels =
    LOAN_ACCOUNT_STATUS_UNPAID_COMMITMENT_AMOUNT_LABEL_TO_LOAN_TYPE_MAP[
      loanType
    ] ?? [];

  const LOAN_TYPE_TO_TITLE_MAP: Record<ExtendedLoanType, string> = {
    TERM_LOAN: "Current Loan Account Status",
    RETAIL_OD: "Current Loan Account Status",
    RETAIL_OD_DRAWDOWN: "Current Outstanding",
  };

  return (
    <>
      <DatafieldSection
        title={LOAN_TYPE_TO_TITLE_MAP[loanType]}
        labels={[
          {
            items: leftLabels,
          },
          {
            items: unpaidCommitmentAmountLabels,
          },
        ]}
        data={{
          ...data,
          unpaidCommitmentAmount: unpaidRepaymentCommitmentAmount,
        }}
      />
    </>
  );
}
